import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Calculator, Activity, Salad } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './CalorieNeedsCalculatorPage/components/CalculationForm';
import ResultsOverview from './CalorieNeedsCalculatorPage/components/ResultsOverview';
import NutritionPlan from './CalorieNeedsCalculatorPage/components/NutritionPlan';
import QuickAdjustmentSidebar from './CalorieNeedsCalculatorPage/components/QuickAdjustmentSidebar';
import WeightProgressSidebar from './CalorieNeedsCalculatorPage/components/WeightProgressSidebar';
import NutritionTipsSidebar from './CalorieNeedsCalculatorPage/components/NutritionTipsSidebar';
import InfoContent from './CalorieNeedsCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  gender: 'female', // 'male' или 'female'
  age: '30',
  weight: '70', // в кг
  height: '165', // в см
  activityLevel: 'low_active', // уровень физической активности
  goal: 'moderate_loss', // цель (поддержание/снижение/набор веса)
  weightLossRate: '0.5', // желаемая скорость снижения веса (кг/неделю)
  excludeFoods: [], // исключенные продукты/группы
  dietType: 'balanced', // тип питания (сбалансированный, низкоуглеводный и т.д.)
  mealsPerDay: '3', // количество приемов пищи в день
  includeSnacks: true,
  considerMedicalConditions: false,
  medicalConditions: [], // заболевания, влияющие на питание
};

// Определение активностей
export const activityLevels = [
  { id: 'sedentary', name: 'Сидячий образ жизни', factor: 1.2, description: 'Минимальная активность, работа за столом, без тренировок' },
  { id: 'low_active', name: 'Малоподвижный', factor: 1.375, description: '1-3 легкие тренировки в неделю' },
  { id: 'moderate', name: 'Умеренно активный', factor: 1.55, description: '3-5 тренировок средней интенсивности в неделю' },
  { id: 'active', name: 'Активный', factor: 1.725, description: '6-7 интенсивных тренировок в неделю' },
  { id: 'very_active', name: 'Очень активный', factor: 1.9, description: 'Физическая работа или 2 тренировки в день' }
];

// Определение целей
export const weightGoals = [
  { id: 'maintain', name: 'Поддержание веса', deficit: 0, description: 'Сохранение текущего веса' },
  { id: 'mild_loss', name: 'Легкое снижение', deficit: 0.25, description: 'Медленное снижение веса (-0.25 кг/неделю)' },
  { id: 'moderate_loss', name: 'Умеренное снижение', deficit: 0.5, description: 'Рекомендуемая скорость снижения (-0.5 кг/неделю)' },
  { id: 'aggressive_loss', name: 'Активное снижение', deficit: 1, description: 'Быстрое снижение веса (-1 кг/неделю)' },
  { id: 'mild_gain', name: 'Легкий набор', surplus: 0.25, description: 'Медленный набор веса (+0.25 кг/неделю)' },
  { id: 'moderate_gain', name: 'Умеренный набор', surplus: 0.5, description: 'Набор мышечной массы (+0.5 кг/неделю)' }
];

// Определение типов диет
export const dietTypes = [
  { id: 'balanced', name: 'Сбалансированное питание', 
    macros: { protein: 0.3, fat: 0.3, carbs: 0.4 },
    description: 'Классическое распределение БЖУ в соотношении 30/30/40' },
  { id: 'low_carb', name: 'Низкоуглеводная диета', 
    macros: { protein: 0.35, fat: 0.45, carbs: 0.2 },
    description: 'Сниженное количество углеводов, больше белка и жиров' },
  { id: 'keto', name: 'Кетогенная диета', 
    macros: { protein: 0.25, fat: 0.7, carbs: 0.05 },
    description: 'Минимум углеводов, преимущественно жиры' },
  { id: 'high_protein', name: 'Высокобелковая диета', 
    macros: { protein: 0.4, fat: 0.3, carbs: 0.3 },
    description: 'Увеличенное количество белка для роста и восстановления мышц' },
  { id: 'mediterranean', name: 'Средиземноморская диета', 
    macros: { protein: 0.25, fat: 0.35, carbs: 0.4 },
    description: 'Баланс с преобладанием полезных жиров и цельных углеводов' }
];

/**
 * CalorieNeedsCalculatorPage Component - Полный калькулятор потребности в калориях с учетом целей по снижению веса.
 */
const CalorieNeedsCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('calorie-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор калорий для похудения | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор калорийности рациона: расчет индивидуальной потребности в калориях, создание плана питания для снижения веса с учетом активности, возраста, пола и целей по снижению веса.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProfiles, setSavedProfiles] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных профилей из localStorage ---
  useEffect(() => {
    const savedProfilesData = localStorage.getItem('savedCalorieProfiles');
    if (savedProfilesData) {
      try {
        setSavedProfiles(JSON.parse(savedProfilesData));
      } catch (e) {
        console.error("Error loading saved profiles:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'lose-weight':
          newState.goal = 'moderate_loss';
          break;
        case 'gain-weight':
          newState.goal = 'moderate_gain';
          break;
        case 'maintain-weight':
          newState.goal = 'maintain';
          break;
        case 'keto-diet':
          newState.dietType = 'keto';
          break;
        case 'low-carb':
          newState.dietType = 'low_carb';
          break;
        case 'high-protein':
          newState.dietType = 'high_protein';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setNutritionPlan(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение профиля в localStorage
  const saveProfile = (name) => {
    if (!results) return;

    const newSavedProfile = {
      id: Date.now().toString(),
      name: name || `Профиль ${formData.weight}кг, ${formData.age} лет`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProfiles = [...savedProfiles, newSavedProfile];
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedCalorieProfiles', JSON.stringify(updatedSavedProfiles));

    return newSavedProfile;
  };

  // Удаление сохраненного профиля
  const deleteSavedProfile = (id) => {
    const updatedSavedProfiles = savedProfiles.filter(profile => profile.id !== id);
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedCalorieProfiles', JSON.stringify(updatedSavedProfiles));
  };

  // Загрузка сохраненного профиля
  const loadSavedProfile = (id) => {
    const profileToLoad = savedProfiles.find(profile => profile.id === id);
    if (profileToLoad) {
      setFormData(profileToLoad.formData);
      setResults(profileToLoad.results);
      // В реальном приложении также потребуется обновить план питания
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calculator className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор калорий для похудения'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              setNutritionPlan={setNutritionPlan}
              activityLevels={activityLevels}
              weightGoals={weightGoals}
              dietTypes={dietTypes}
              saveProfile={saveProfile}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <NutritionPlan 
                  nutritionPlan={nutritionPlan} 
                  results={results}
                  formData={formData}
                />
              </>
            )}

            {/* --- Информационный контент о правильном питании --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              activityLevels={activityLevels}
              weightGoals={weightGoals}
            />

            {/* --- Панель прогресса снижения веса --- */}
            <WeightProgressSidebar 
              savedProfiles={savedProfiles}
              deleteSavedProfile={deleteSavedProfile}
              loadSavedProfile={loadSavedProfile}
              currentResults={results}
            />

            {/* --- Панель полезных советов --- */}
            <NutritionTipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Nutrition Plan Table Scrollbar */
        .nutrition-plan-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .nutrition-plan-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .nutrition-plan-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .nutrition-plan-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .nutrition-plan-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .nutrition-plan-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .nutrition-plan-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .nutrition-plan-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .nutrition-plan-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default CalorieNeedsCalculatorPage;